import { useState, useRef, useEffect } from 'react'
import './Examples.css'

interface Example {
  name: string
  description: string
  code: string
}

interface ExamplesProps {
  onSelect: (code: string) => void
}

const examples: Example[] = [
  {
    name: 'Hello World',
    description: 'A simple introduction to Jertscript',
    code: `// Hello World in Jertscript
// "Honestly, JavaScript could use more croissants." 🥐

// Declare an immutable variable (like const)
croissant message = "Bonjour, le monde!";

// Print the message (like console.log)
dire(message);

// Declare a mutable variable (like let)
baguette count = 0;

// A simple loop
pendant (count < 3) {
    dire("Vive la France!");
    count = count + 1;
}

// Boolean literals
croissant isAwesome = oui;
croissant isBoring = non;

// Conditional statement
franchement (isAwesome est oui) {
    dire("Jertscript is magnifique!");
} sinon {
    dire("This should never happen.");
}
`
  },
  {
    name: 'Fibonacci',
    description: 'Recursive fibonacci avec une touche francaise!',
    code: `// Fibonacci in Jertscript
// A classic recursive algorithm avec une touche francaise! 🥐

// Recursive fibonacci function
// Uses franchement (if) for base cases and retourner (return) for values
fonction fibonacci(n) {
    // Base cases: fib(0) = 0, fib(1) = 1
    franchement (n est 0) {
        retourner 0;
    }
    franchement (n est 1) {
        retourner 1;
    }

    // Recursive case: fib(n) = fib(n-1) + fib(n-2)
    retourner fibonacci(n - 1) + fibonacci(n - 2);
}

// Print the first 10 fibonacci numbers
dire("Les premiers 10 nombres de Fibonacci:");

baguette i = 0;
pendant (i < 10) {
    croissant result = fibonacci(i);
    dire(result);
    i = i + 1;
}

dire("Magnifique! C'est les mathematiques a la francaise!");
`
  },
  {
    name: 'Variables',
    description: 'Demonstrating croissant, baguette, and literals',
    code: `// Variables Example - Jertscript
// Demonstrating croissant (const), baguette (let), and literal values

// ============================================
// CROISSANT (const) - Immutable Variables
// ============================================
// In Jertscript, 'croissant' creates a constant (like 'const' in JavaScript)
// Once set, a croissant cannot be changed - it is immutable, like French pride!

croissant nom = "Jean";
croissant age = 42;
croissant piValue = 3.14159;

dire("=== Les Croissants (Constants) ===");
dire("Nom: " + nom);
dire("Age: " + age);
dire("Pi: " + piValue);

// ============================================
// BAGUETTE (let) - Mutable Variables
// ============================================
// In Jertscript, 'baguette' creates a mutable variable (like 'let' in JavaScript)
// A baguette can be changed - it is flexible, like a fresh baguette!

baguette compteur = 0;
baguette message = "Bonjour";

dire("");
dire("=== Les Baguettes (Let Variables) ===");
dire("Compteur initial: " + compteur);

// Baguettes can be reassigned
compteur = compteur + 1;
dire("Compteur apres +1: " + compteur);

compteur = compteur + 1;
dire("Compteur apres +1: " + compteur);

message = "Au revoir";
dire("Message change: " + message);

// ============================================
// OUI (true) and NON (false) - Boolean Literals
// ============================================
// In Jertscript, 'oui' means true and 'non' means false
// Because in France, we say things with style!

croissant estFrancais = oui;
croissant estEnnuyeux = non;

dire("");
dire("=== Oui et Non (Booleans) ===");
dire("Est francais? " + estFrancais);
dire("Est ennuyeux? " + estEnnuyeux);

// Using booleans in conditions
franchement (estFrancais est oui) {
    dire("Vive la France!");
}

franchement (estEnnuyeux est non) {
    dire("Jertscript is never boring!");
}

// ============================================
// RIEN (null) - Null Literal
// ============================================
// In Jertscript, 'rien' means null (nothing)
// Sometimes, there is simply... rien!

baguette valeurVide = rien;

dire("");
dire("=== Rien (Null) ===");
dire("Valeur vide: " + valeurVide);

// Check for null
franchement (valeurVide est rien) {
    dire("La valeur est rien (null)!");
}

// Assign a value to replace rien
valeurVide = "Maintenant j'ai une valeur!";
dire("Apres assignation: " + valeurVide);
`
  },
  {
    name: 'Try/Catch',
    description: 'Error handling with malheureusement/merde/quandmeme',
    code: `// Error Handling in Jertscript
// Using malheureusement (try), merde (catch), and quandmeme (finally)

dire("=== Error Handling Example ===");

// Function that might fail
fonction riskyOperation(value) {
    franchement (value < 0) {
        // In a real scenario, this would throw an error
        dire("Merde! Value cannot be negative!");
        retourner rien;
    }
    retourner value * 2;
}

// Try handling errors gracefully
malheureusement {
    dire("Attempting risky operation...");
    croissant result = riskyOperation(5);
    dire("Result: " + result);
} merde (error) {
    dire("Caught an error: " + error);
} quandmeme {
    dire("This always runs, error or not!");
}

dire("");
dire("=== Another Example ===");

malheureusement {
    croissant badResult = riskyOperation(-1);
    franchement (badResult est rien) {
        dire("Operation failed gracefully");
    }
} quandmeme {
    dire("Cleanup complete. Au revoir!");
}
`
  }
]

export function Examples({ onSelect }: ExamplesProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelect = (example: Example) => {
    onSelect(example.code)
    setIsOpen(false)
  }

  return (
    <div className="examples-dropdown" ref={dropdownRef}>
      <button
        className="examples-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        Menu du Jour
        <span className={`examples-arrow ${isOpen ? 'open' : ''}`}>
          ▼
        </span>
      </button>

      {isOpen && (
        <div className="examples-menu">
          <div className="examples-header">
            ~ Prix Fixe ~
          </div>
          {examples.map((example, index) => (
            <button
              key={index}
              className="example-item"
              onClick={() => handleSelect(example)}
            >
              <span className="example-name">{example.name}</span>
              <span className="example-description">{example.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export { examples }
export type { Example }
